import React, { useState, useRef, useEffect } from 'react';
import axios from '../utils/axiosInstance';
import './CreateList.css';

const isAdmin = () => {
  try { return localStorage.getItem('isAdmin') === 'true'; } catch { return false; }
};

/* ---------- network helpers ---------- */
function buildFormData({ name, items, imageFile }) {
  const fd = new FormData();
  fd.append('listName', name);
  fd.append('name', name);
  items.forEach((t) => fd.append('items[]', t));
  if (imageFile) fd.append('image', imageFile);
  return fd;
}

async function createDirectList({ name, items, imageFile }) {
  const fd = buildFormData({ name, items, imageFile });

  const tries = [
    () => axios.post('/rankings', fd),
    () => axios.post('/lists', fd),
    () => axios.post('/admin/lists', fd),
  ];

  const json = { listName: name, name, items };
  tries.push(() => axios.post('/rankings', json));
  tries.push(() => axios.post('/lists', json));
  tries.push(() => axios.post('/admin/lists', json));

  let err;
  for (const fn of tries) {
    try { const res = await fn(); return res?.data; } catch (e) { err = e; }
  }
  throw err || new Error('No matching admin create endpoint');
}

/* CHANGED: lock user submission to /suggestions only (no fallbacks) */
async function submitSuggestion({ name, items, imageFile }) {
  const payload = imageFile
    ? buildFormData({ name, items, imageFile })
    : { name, items };
  const { data } = await axios.post('/suggestions', payload);
  return data;
}

/* ---------- MusicBrainz importer ---------- */
const MBZ_MODES = {
  NONE: '',
  ALBUM_SONGS: 'album-songs',
  ARTIST_ALBUMS: 'artist-albums',
  ARTIST_SONGS: 'artist-songs',
};

const CreateList = () => {
  const [name, setName] = useState('');
  const [items, setItems] = useState(['', '', '', '', '']); // 5 defaults
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // importer state
  const [importMode, setImportMode] = useState(MBZ_MODES.NONE);
  const [appendMode, setAppendMode] = useState(false);
  const [search, setSearch] = useState('');
  const [suggest, setSuggest] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [debounceId, setDebounceId] = useState(null);

  // clear msgs when mode changes
  useEffect(() => { setMsg(''); setErr(''); setSearch(''); setSuggest([]); }, [importMode]);

  const updateItem = (idx, value) => {
    const next = [...items];
    next[idx] = value;
    setItems(next);
  };
  const addRow = () => setItems((prev) => [...prev, '']);
  const removeRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const onPickImage = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setImageFile(f);
    const url = URL.createObjectURL(f);
    setImagePreview((old) => { if (old) URL.revokeObjectURL(old); return url; });
  };
  const onRemoveImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* NEW: clear list items to initial 5 blanks */
  const clearListOptions = () => {
    setItems(['', '', '', '', '']);
    setMsg('');
    setErr('');
  };

  /* importer: typeahead */
  const placeholder =
    importMode === MBZ_MODES.ALBUM_SONGS ? 'Search for an album…' :
    importMode === MBZ_MODES.ARTIST_ALBUMS ? 'Search for an artist…' :
    importMode === MBZ_MODES.ARTIST_SONGS ? 'Search for an artist…' :
    'Select a mode first';

  const onSearchChange = (val) => {
    setSearch(val);
    setSuggest([]);
    if (debounceId) clearTimeout(debounceId);
    if (!val || val.trim().length < 2 || !importMode) return;

    const id = setTimeout(async () => {
      try {
        setLoadingSuggest(true);
        if (importMode === MBZ_MODES.ALBUM_SONGS) {
          const { data } = await axios.get('/music/search/release', { params: { q: val.trim() } });
          // "album - artist - year"
          setSuggest(data.map(r => ({
            id: r.id,
            label: [r.title, r.artist, r.year].filter(Boolean).join(' - '),
          })));
        } else {
          const { data } = await axios.get('/music/search/artist', { params: { q: val.trim() } });
          // "artist name - country"
          setSuggest(data.map(a => ({
            id: a.id,
            label: [a.name, a.country].filter(Boolean).join(' - '),
          })));
        }
      } catch (e) {
        console.error('suggest error', e);
      } finally {
        setLoadingSuggest(false);
      }
    }, 350);
    setDebounceId(id);
  };

  const applyImported = (titles, src) => {
    const cleaned = (titles || []).map(t => (t || '').trim()).filter(Boolean);
    const seen = new Set();
    const unique = cleaned.filter(t => {
      const k = t.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    setItems(prev => appendMode ? [...prev.filter(Boolean), ...unique] : [...unique]);
    setMsg(`✅ Imported ${unique.length} item(s) from ${src}.`);
    setErr('');
  };

  const onPickSuggestion = async (opt) => {
    if (!opt?.id) return;
    try {
      setBusy(true);
      if (importMode === MBZ_MODES.ALBUM_SONGS) {
        const { data } = await axios.get(`/music/release/${opt.id}/tracks`);
        applyImported(data.tracks, `album “${data.title || opt.label}”`);
      } else if (importMode === MBZ_MODES.ARTIST_ALBUMS) {
        const { data } = await axios.get(`/music/artist/${opt.id}/albums`);
        applyImported(data.titles, 'artist albums');
      } else if (importMode === MBZ_MODES.ARTIST_SONGS) {
        const { data } = await axios.get(`/music/artist/${opt.id}/recordings`);
        applyImported(data.titles, 'artist songs');
      }
      setSuggest([]);
      setSearch('');
    } catch (e) {
      console.error('import error', e);
      const apiErr = e?.response?.data?.error || 'Failed to import from MusicBrainz.';
      setErr(apiErr);
      setMsg('');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');

    const cleaned = items.map((x) => (x || '').trim()).filter(Boolean);
    const trimmed = name.trim();
    if (!trimmed) return setErr('Please enter a list name.');
    if (cleaned.length < 2) return setErr('Please add at least 2 non-empty items.');

    try {
      setBusy(true);
      if (isAdmin()) {
        await createDirectList({ name: trimmed, items: cleaned, imageFile });
        setMsg('✅ List created.');
        setName('');
        setItems(['', '', '', '', '']);
        onRemoveImage();
        setTimeout(() => { window.location.assign('/admin/lists'); }, 500);
      } else {
        await submitSuggestion({ name: trimmed, items: cleaned, imageFile });
        setMsg('✅ List created and available to rank!');
        setName('');
        setItems(['', '', '', '', '']);
        onRemoveImage();
      }
    } catch (e2) {
      console.error('Create list error:', e2);
      const apiErr = e2?.response?.data?.error || e2?.response?.data?.message || 'Failed to submit. Please try again.';
      setErr(apiErr);
    } finally {
      setBusy(false);
    }
  };

  const onItemKeyDown = (idx, e) => {
    if (e.key === 'Enter') {
      if (idx === items.length - 1) {
        e.preventDefault();
        addRow();
      }
    }
  };

  // Mobile check (no extra hooks — computed per render)
  const isMobile = (typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(max-width: 768px)').matches);

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        {/* MOBILE-ONLY OUTLINE WRAPPER (border sits on the white card itself) */}
        <div>
          <div
            className="create-wrap"
            style={isMobile ? { border: '2px solid #0e0e0e', borderRadius: 12, padding: 12 } : undefined}
          >

            {/* MusicBrainz Importer */}
            <div className="mbz-import">
              <div className="mbz-row">
                <label className="mbz-label">Use the search bar to import an album's songs or an artist's entire discography, where you can rank all their songs or all their albums!</label>
                <div className="mbz-controls">
                  <select
                    className="mbz-select"
                    value={importMode}
                    onChange={(e) => setImportMode(e.target.value)}
                  >
                    <option value="">Select…</option>
                    <option value={MBZ_MODES.ALBUM_SONGS}>Album’s Songs</option>
                    <option value={MBZ_MODES.ARTIST_ALBUMS}>Artist’s Albums</option>
                    <option value={MBZ_MODES.ARTIST_SONGS}>Artist’s Songs</option>
                  </select>

                  <label className="mbz-append">
                    <input
                      type="checkbox"
                      checked={appendMode}
                      onChange={(e) => setAppendMode(e.target.checked)}
                    />
                    <span>Append (don’t replace)</span>
                  </label>
                </div>
              </div>

              <div className="mbz-row">
                <input
                  className="mbz-search"
                  placeholder={
                    importMode
                      ? (importMode === MBZ_MODES.ALBUM_SONGS ? 'Search for an album…' : 'Search for an artist…')
                      : 'Select a mode first'
                  }
                  disabled={!importMode}
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>

              {!!importMode && !!search && (loadingSuggest || suggest.length > 0) && (
                <div className="mbz-suggest">
                  {loadingSuggest && <div className="mbz-suggest-item muted">Searching…</div>}
                  {!loadingSuggest && suggest.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className="mbz-suggest-item"
                      onClick={() => onPickSuggestion(opt)}
                      title="Use this result"
                    >
                      {opt.label}
                    </button>
                  ))}
                  {!loadingSuggest && suggest.length === 0 && (
                    <div className="mbz-suggest-item muted">No results</div>
                  )}
                </div>
              )}
            </div>

            <h2 className="create-title">Create List</h2>

            {/* Image preview + buttons */}
            <div className="image-uploader">
              <div className="image-square">
                {imagePreview ? (
                  <img src={imagePreview} alt="Selected" />
                ) : (
                  <div className="image-placeholder">No image selected</div>
                )}
              </div>

              <div className="image-actions">
                <input
                  ref={fileInputRef}
                  id="list-image"
                  type="file"
                  accept="image/*"
                  onChange={onPickImage}
                  style={{ display: 'none' }}
                />

                {/* Upload (green) */}
                <button
                  type="button"
                  className="btn btn-upload"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  Upload image
                </button>

                {/* NEW: Clear list (red) */}
                <button
                  type="button"
                  className="btn btn-danger btn-clear"
                  onClick={clearListOptions}
                  aria-label="Clear all list items"
                >
                  Clear list
                </button>

                {/* Remove image (red) — only when an image is selected */}
                {imagePreview && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-remove"
                    onClick={onRemoveImage}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            {msg && <div className="notice success" role="status" aria-live="polite">{msg}</div>}
            {err && <div className="notice error" role="alert">{err}</div>}

            <form className="create-form" onSubmit={handleSubmit}>
              <label className="field">
                <span className="label">List name</span>
                <input
                  className="input"
                  placeholder="e.g., Best Snacks"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                />
              </label>

              <div className="items-header">
                <span>Items</span>
                <button type="button" className="btn btn-ghost add-item-btn" onClick={addRow}>
                  + Add item
                </button>
              </div>

              <div className="items-rows">
                {items.map((value, idx) => (
                  <div className="item-row" key={idx}>
                    <input
                      className="input"
                      placeholder={`Item ${idx + 1}`}
                      value={value}
                      onChange={(e) => updateItem(idx, e.target.value)}
                      onKeyDown={(e) => onItemKeyDown(idx, e)}
                      maxLength={80}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost danger remove-item-btn"
                      onClick={() => removeRow(idx)}
                      aria-label={`Remove item ${idx + 1}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Sending…' : 'Create'}
                </button>
              </div>
            </form>

            {!isAdmin() && (
              <p className="helper">
                Your list is now available to rank!
              </p>
            )}
          </div>
        </div>
        {/* END MOBILE-ONLY OUTLINE WRAPPER */}
      </div>
    </div>
  );
};

export default CreateList;
